import { Download, Send } from 'lucide-react'
import { useEffect, useMemo, useRef, type FormEvent } from 'react'
import { isApiError } from '../../api/client'
import { startErrorText, useJob, useStartReel, useToolsInfo, type ReelResult } from '../../api/tools'
import { downloadFile } from '../../lib/download'
import { instagramCode, parseLinks } from '../../lib/links'
import { useViewState } from '../../lib/navigation'
import { isInTelegram } from '../../telegram'
import { Button } from '../Button'
import { Dialog } from '../Dialog'
import { Spinner } from '../Spinner'
import { JobProgress, JobResult } from './JobView'
import { LinksField } from './LinksField'

/** «Скачать Reels»: в браузере mp4 на устройство, в Mini App — в чат с ботом. */
export function ReelDialog({ stateKey, initial = '', onClose: finishClose }: { stateKey: string; initial?: string; onClose: () => void }) {
  const info = useToolsInfo()
  const start = useStartReel()
  const [text, setText] = useViewState(`${stateKey}.text`, initial)
  const [jobId, setJobId] = useViewState<string | null>(`${stateKey}.job`, null)
  const job = useJob(jobId)
  const [downloadedJob, setDownloadedJob] = useViewState<string | null>(`${stateKey}.downloaded`, null)
  const downloadedNow = useRef(downloadedJob)
  const onClose = () => { setText(''); setJobId(null); setDownloadedJob(null); finishClose() }

  // В Mini App WebView файл не сохранит: шлём в чат, если бот включён.
  const toChat = isInTelegram() && info.data?.telegram === true
  const parsed = useMemo(() => parseLinks(text, instagramCode), [text])
  const ready = parsed.ids.length === 1 && parsed.bad.length === 0

  const data = job.data
  useEffect(() => {
    if (!data || data.status !== 'done' || downloadedJob === data.id || downloadedNow.current === data.id) return
    const url = (data.result as ReelResult).download_url
    if (!url) return
    downloadedNow.current = data.id
    setDownloadedJob(data.id)
    const r = data.result as ReelResult
    downloadFile(url, `${r.handle}_${r.post_id}.mp4`)
  }, [data, downloadedJob, setDownloadedJob])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!ready || start.isPending) return
    start.mutate(
      { url: parsed.lines[0] ?? '', deliver: toChat ? 'telegram' : 'browser' },
      { onSuccess: (created) => setJobId(created.id) },
    )
  }

  const finished = data?.status === 'done' || data?.status === 'failed'

  return (
    <Dialog
      title="Скачать Reels"
      onClose={onClose}
      footer={
        jobId ? (
          <Button variant={finished ? 'primary' : 'ghost'} onClick={onClose}>
            {finished ? 'Готово' : 'Свернуть'}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button variant="primary" type="submit" form="tools-reel" disabled={!ready || start.isPending}>
              {start.isPending ? (
                <Spinner size={14} className="text-accent-fg" />
              ) : toChat ? (
                <Send size={14} />
              ) : (
                <Download size={14} />
              )}
              {toChat ? 'Отправить в чат' : 'Скачать'}
            </Button>
          </>
        )
      }
    >
      {!jobId ? (
        <form id="tools-reel" onSubmit={submit} className="space-y-4">
          <p className="text-sm text-pretty text-muted">
            {toChat
              ? 'Вставьте ссылку на Reel из Instagram. Мы подготовим mp4 и отправим его в чат с ботом.'
              : 'Вставьте ссылку на Reel из Instagram. Мы подготовим mp4 и сохраним его на ваше устройство.'}
          </p>
          <LinksField
            single
            label="Ссылка на видео"
            value={text}
            onChange={setText}
            parsed={parsed}
            placeholder="https://www.instagram.com/reel/…"
            hint={toChat ? 'Отправлю в чат с ботом' : 'Ролик, который уже есть в заводе, скачается сразу'}
            kindLabel="пост Instagram"
            max={1}
          />
          {start.isError && (
            <p className="text-sm text-danger">{startErrorText(isApiError(start.error) ? start.error.detail : null)}</p>
          )}
        </form>
      ) : !data ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : finished ? (
        <JobResult job={data} />
      ) : (
        <JobProgress job={data} />
      )}
    </Dialog>
  )
}
